import React, { useCallback, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { skipToken } from '@reduxjs/toolkit/query';
import { setSubpage } from '../../../routes/routerSlice';
import {
  computeFontSize,
  ConfigurationSwitch,
  GridCol,
  GridRow,
  MainContent,
  RowCentered,
  Section,
} from '@ydistri/ds';
import BalancingFieldsTable from './BalancingFieldsTable';
import { ConfigurationSubpage } from '../Configuration';
import { BalancingGroupKey, BalancingVersions, versionedBalancingGroups } from './balancingLib';
import { useGetBalancingConfigurationQuery } from './apiBalancingConfiguration';
import LoadingIndicator from '../../../components/global/LoadingIndicator/LoadingIndicator';
import {
  UpdateCalculationConfigurationPayload,
  useGetCalculationConfigurationQuery,
  useUpdateCalculationConfigurationMutation,
} from '../../../apis/apiGlobalConfiguration';
import {
  ConfigurationFieldStatus,
  rtkQueryPatchStatusToConfigurationFieldStatus,
} from '@ydistri/utils';
import { useUser } from '../../../hooks/useUser';
import { useAppDispatch } from '../../../store';
import useIsConfigurationEditable from '../../../hooks/useIsConfigurationEditable';
import { useTemplateOrCalculationWithValue } from '../../../hooks/useTemplateOrCalculationWithValue';

const ConfigurationBalancing: React.FC = () => {
  const dispatch = useAppDispatch();
  const templateOrCalculation = useTemplateOrCalculationWithValue();
  const isConfigurationEditable = useIsConfigurationEditable();
  const user = useUser();

  const { data: balancingData, isFetching } = useGetBalancingConfigurationQuery(
    templateOrCalculation.value,
  );
  const { data: calculationConfiguration, isFetching: isFetchingCalculationConfiguration } =
    useGetCalculationConfigurationQuery(templateOrCalculation.value);
  const [updateConfiguration, updateConfigurationStatus] =
    useUpdateCalculationConfigurationMutation();

  useEffect(() => {
    dispatch(setSubpage(ConfigurationSubpage.ADVANCED));
  }, [dispatch]);

  const onChangeExperimentalModeHandler = useCallback(
    (value: string) => {
      if (templateOrCalculation.hasValidValue && templateOrCalculation.value !== skipToken) {
        const payload: UpdateCalculationConfigurationPayload = {
          templateOrCalculation: templateOrCalculation.value,
          request: {
            isExperimentConfigurationsEnabled: value === '1',
          },
        };

        updateConfiguration(payload);
      }
    },
    [templateOrCalculation, updateConfiguration],
  );

  const status =
    isFetching || isFetchingCalculationConfiguration
      ? ConfigurationFieldStatus.LOADING
      : rtkQueryPatchStatusToConfigurationFieldStatus(updateConfigurationStatus);

  const version = balancingData?.version;
  const isExperimentalConfigEnabled =
    calculationConfiguration?.isExperimentConfigurationsEnabled ?? false;

  const headerActionForEnabledSection = useMemo(
    () =>
      version ? (
        <RowCentered $gap="1rem">
          Enable advanced configurations
          <ConfigurationSwitch
            disabled={
              !(
                (isConfigurationEditable || user?.isSuperUser) &&
                templateOrCalculation.hasValidValue
              )
            }
            status={status}
            value={isExperimentalConfigEnabled}
            onChange={onChangeExperimentalModeHandler}
          />
        </RowCentered>
      ) : (
        'This version was not recognized'
      ),
    [
      version,
      isConfigurationEditable,
      user?.isSuperUser,
      templateOrCalculation.hasValidValue,
      status,
      isExperimentalConfigEnabled,
      onChangeExperimentalModeHandler,
    ],
  );

  const balancingGroups = versionedBalancingGroups[version ?? BalancingVersions.V_UNKNOWN];

  if (isFetching) return <LoadingIndicator />;

  return (
    <>
      <Helmet title="Advanced configuration" />
      <MainContent>
        <Section
          header="Advanced configurations"
          headerActions={headerActionForEnabledSection}
          $ratio={0}
        />
        {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- may be undefined */}
        {version && balancingGroups && isExperimentalConfigEnabled && (
          <GridRow>
            <GridCol span={24}>
              <GridRow gutter={[0, computeFontSize()]}>
                {Object.keys(balancingGroups).map(g => {
                  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we need to type this
                  const groupKey = g as unknown as BalancingGroupKey;
                  const group = balancingGroups[groupKey];
                  if (group) {
                    return (
                      <GridCol span={24} lg={12} xl={10} key={group.id}>
                        <Section header={group.header} $height="100%">
                          <BalancingFieldsTable groups={balancingGroups} groupId={group.id} />
                        </Section>
                      </GridCol>
                    );
                  } else {
                    return null;
                  }
                })}
              </GridRow>
            </GridCol>
          </GridRow>
        )}
      </MainContent>
    </>
  );
};

export default ConfigurationBalancing;
